// Field units (2026-10-08): a unit covers ONE domain (as before) or a whole
// field, "Whole Math" / "Whole Reading & Writing", where the backend picks
// each student's own domain (no teacher override) and locks it for the unit.
//
// Contract (claude-teacher-api): a unit summary carries `scope: 'field'` plus
// `subject` and NO domain (domainKey null); per-student rows (roster,
// gradebook, live state, stuck lists, home snapshot students) carry
// `unitDomain` in frontend key form (algebra, psda, advanced, geotrig, info,
// craft, conventions, expression). A missing scope is a domain unit, and a
// domain unit must read and render exactly as it did before this file.
import { DOMAINS, subjectLabel } from './mockData';

export const FIELD_SUBJECTS = ['math', 'reading'];
const ALL_DOMAINS = [...DOMAINS.math, ...DOMAINS.reading];
const byKey = (key) => ALL_DOMAINS.find((d) => d.key === key) || null;

export const isFieldUnit = (unit) => !!unit && unit.scope === 'field';

// The unit's domain key for a domain unit; null for a field unit (each
// student has their own: see studentUnitDomain).
export const unitDomainKey = (unit) => (!unit || isFieldUnit(unit) ? null : (unit.domain ?? unit.domainKey ?? null));

export const subjectOfDomainKey = (key) => (DOMAINS.reading.some((d) => d.key === key) ? 'reading' : 'math');
export const unitSubject = (unit) => (FIELD_SUBJECTS.includes(unit?.subject) ? unit.subject : subjectOfDomainKey(unitDomainKey(unit)));

// "Math" / "Reading & Writing" (the gradebook tab form).
export const fieldLabel = (subject) => subjectLabel(subject === 'reading' ? 'reading' : 'math');
// "Whole Math" / "Whole Reading & Writing" (pickers, headers, toasts).
export const fieldUnitTitle = (subject) => `Whole ${fieldLabel(subject)}`;

// Long name of a unit: "Algebra" or "Whole Math".
export function unitTitle(unit, fallback = null) {
  if (!unit) return fallback;
  if (isFieldUnit(unit)) return fieldUnitTitle(unit.subject);
  const key = unitDomainKey(unit);
  return byKey(key)?.full || fallback || key || 'Current unit';
}

// Short name of a unit for pills: "Geo/Trig" or "Math".
export function unitShort(unit, fallback = null) {
  if (!unit) return fallback;
  if (isFieldUnit(unit)) return fieldLabel(unit.subject);
  const key = unitDomainKey(unit);
  return byKey(key)?.short || fallback || key || 'Unit';
}

// The domain one student works in for this unit (null when a field unit has
// not chosen theirs yet, e.g. a late joiner before their first read).
export function studentUnitDomain(student, unit) {
  if (isFieldUnit(unit)) return byKey(student?.unitDomain) ? student.unitDomain : null;
  return unitDomainKey(unit);
}

export const domainFull = (key) => byKey(key)?.full || null;
export const domainShortOf = (key) => byKey(key)?.short || null;

// Labels for the Home split line ("Algebra 9 · Advanced 7 · Geometry 6 · Data 3").
const SPLIT_LABEL = {
  algebra: 'Algebra', advanced: 'Advanced', geotrig: 'Geometry', psda: 'Data',
  info: 'Info', craft: 'Craft', conventions: 'Conventions', expression: 'Expression',
};

// Per-domain head count of a field unit, most students first; ties keep the
// subject's canonical domain order. [] for a domain unit.
export function domainSplit(students, unit) {
  if (!isFieldUnit(unit)) return [];
  const counts = new Map();
  (students || []).forEach((s) => {
    const key = studentUnitDomain(s, unit);
    if (key) counts.set(key, (counts.get(key) || 0) + 1);
  });
  const order = DOMAINS[unit.subject] || ALL_DOMAINS;
  const rank = (key) => { const i = order.findIndex((d) => d.key === key); return i < 0 ? order.length : i; };
  return [...counts.entries()]
    .map(([key, count]) => ({ key, count, label: SPLIT_LABEL[key] || byKey(key)?.short || key, full: byKey(key)?.full || key }))
    .sort((a, b) => b.count - a.count || rank(a.key) - rank(b.key));
}

export const splitLine = (students, unit) => domainSplit(students, unit).map((d) => `${d.label} ${d.count}`).join(' · ');

// The domain key to send with getHomeSnapshot: the unit's own domain, or a
// valid key of the field's subject so the backend's validation passes (the
// backend reads the active field unit itself; see fetchHomeSnapshot's field hint).
export function snapshotDomainFor(unit, fallback = 'algebra') {
  if (!unit) return fallback;
  if (isFieldUnit(unit)) return DOMAINS[unit.subject]?.[0]?.key || fallback;
  return unitDomainKey(unit) || fallback;
}
